<template>
    <modal name="feedbackModal" class="feedbackModal" :height="'auto'" :width="'40%'">
        <form class="feedback-form" @submit.prevent="handleSubmit">
            <div class="modal-header">
                <h3 class="title">
                    {{ $t('finish_feedback') }}
                </h3>
                <button v-if="feedbackId" type="button" class="action-button" @click="requestDelete">
                    <i class="fas fa-trash"></i>
                </button>
            </div>

            <div class="field text-field">
                <textarea id="feedback-text" :value="feedbackText" @input="handleInput" class="feedback-textarea"
                    :class="{ 'error': showFeedbackError }" rows="4"
                    :placeholder="$t('min_length_placeholder')"></textarea>
                <div v-if="showFeedbackError" class="error-message">
                    {{ $t('feedback_min_length') }}
                </div>
            </div>

            <div class="feedback-table-container">
                <table v-if="formattedPlates.length" class="feedback-table">
                    <thead>
                        <tr>
                            <th>Data</th>
                            <th>Tipo de prato</th>
                            <th>Prato</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(plate, index) in formattedPlates" :key="index">
                            <td>{{ plate.data }}</td>
                            <td>{{ plate.tipoPrato }}</td>
                            <td class="just-text">{{ plate.prato }}</td>
                        </tr>
                    </tbody>
                </table>
                <div v-else class="empty-state">{{ $t('no_feedback_found') }}</div>
            </div>

            <div class="button-div feedback-button-div">
                <button type="button" class="button ghost" @click="handleCancel">
                    <i class="fas fa-times"></i> {{ $t('cancel') }}
                </button>

                <button type="submit" class="button" :disabled="!isValid">
                    {{ $t('send') }}
                </button>
            </div>
        </form>
        <ConfirmModal ref="confirmModal" :message="confirmMessage" :confirm-text="confirmText" :cancel-text="cancelText"
            modal-name="feedbackConfirmModal" @confirm="confirmDelete" @cancel="cancelDelete" />
    </modal>
</template>

<script>
import moment from "moment";
import ConfirmModal from "./ConfirmModal.vue";
// import { t } from 'i18next';

export default {
    name: "FeedbackModal",
    components: { ConfirmModal },
    props: {
        feedbackText: {
            type: String,
            default: ""
        },
        showFeedbackError: {
            type: Boolean,
            default: false
        },
        platesList: {
            type: Array,
            default: () => []
        },
        feedbackId: {
            type: [String, Number],
            default: null
        }
    },
    data() {
        return {
            confirmMessage: "Deseja realmente excluir esse feedback?",
            confirmText: "Sim",
            cancelText: "Não",
        };
    },
    computed: {
        isValid() {
            return this.feedbackText.trim().length >= 30;
        },
        formattedPlates() {
            return (this.platesList || []).map((item) => ({
                data: this.formatDateOnly(item.data || item.DATA || item.DTCARDAPIO || ""),
                tipoPrato: item.NMTIPOPRAT || item.tipoPrato || item.TIPOPRATO || item.nm_tipoprato || "",
                prato: item.nomePrato || item.NMPRATO || item.prato || item.nm_prato || ""
            }));
        }
    },
    methods: {
        formatDateOnly(value) {
            if (!value) return "";
            const parsed = moment(value, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD"], true);
            if (parsed.isValid()) {
                return parsed.format("DD/MM/YYYY");
            }
            return typeof value === "string" ? value.split(" ")[0] : "";
        },
        handleInput(event) {
            this.$emit('update:feedbackText', event.target.value);
        },

        handleSubmit() {
            if (!this.isValid) {
                this.$emit('showError');
                return;
            }
            this.$emit('submit');
        },
        requestDelete() {
            if (!this.feedbackId) return;
            if (this.$refs.confirmModal) {
                this.$refs.confirmModal.open();
            }
        },
        confirmDelete() {
            if (!this.feedbackId) return;
            this.$emit('delete', this.feedbackId);
        },
        cancelDelete() {
            // only closes confirm modal through component
        },

        handleCancel() {
            this.$emit('cancel');
        }
    }
};
</script>

<style scoped lang="less">
@import (reference) url("../../../assets/variables.less");

.feedbackModal {
    z-index: 999 !important;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.action-button {
    font-weight: 500;
    padding: 10px 14px;
    font-size: 1rem;
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.24), 0 0 2px rgba(0, 0, 0, 0.12);
    color: #dc143d;
    border: none;
    cursor: pointer;
    border-radius: 4px;

    &:hover {
        background: #dc143d;
        color: white;
    }
}

.feedback-textarea {
    width: 100%;
    min-height: 120px;
    border-radius: 8px;
    border: 1px solid #dcdcdc;
    font-size: 14px;
    resize: none;
    padding: 8px;
    box-sizing: border-box;
    font-family: inherit;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.feedback-textarea:focus {
    border-color: #e74c3c;
    box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.15);
    outline: none;
}

.feedback-table-container {
    max-height: 55dvh;
    overflow-y: auto;
    overflow-x: hidden;
    border: 1px solid #e7e7e7;
    border-radius: 6px;
}

.feedback-table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 10px 12px;
        border-bottom: 1px solid #e7e7e7;
        text-align: left;
        vertical-align: top;
    }

    td:first-child {
        width: 10%;
    }

    th {
        background: #212146;
        font-weight: 600;
        color: white;
        position: sticky;
        top: 0;
        z-index: 1;
    }

    tbody tr:nth-child(odd) {
        background-color: #e7e7e7;
    }

    tbody tr:nth-child(even) {
        background-color: #ffffff;
    }

    tbody tr:hover {
        background-color: #e8f4f8;
    }

    .just-text {
        white-space: nowrap;
        word-break: break-word;
        max-width: 40dvw;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

.empty-state {
    padding: 12px;
    background: #f7f7f7;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
}

.error-message {
    color: red;
    font-size: 12px;
    margin-top: 5px;
}

.button-div {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
}

.button {
    min-width: 150px;
    padding: 6px 15px;
    margin: auto 3px;
}

.ghost {
    background-color: white;
    box-shadow: none;
    color: #dc143d;
    border: 1px solid #dc143d;
    padding: 5px 14px;

    &:hover {
        color: #212146;
        border: 1px solid #212146;
    }
}

.feedback-form {
    width: calc(100% - 40px);
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
</style>
